/**
 * Per-tag skill estimation, Elo-style.
 *
 * Each tag (category meta-tag, e.g. "World Geography", "U.S. Presidents") and
 * each clue live on a shared 0..1 difficulty/skill scale. When the user grades
 * a clue, we treat it as a match between the user's skill at that tag and the
 * clue's difficulty, and nudge the tag rating toward the observed outcome —
 * exactly like an Elo update, but with the K-factor scaled by uncertainty so
 * early attempts move fast and settle as evidence accrues (Glicko-lite).
 *
 * Outcome scoring is graded, not binary: Known counts as a full win against the
 * clue, Shaky as a partial win, Missed as a loss. This is what lets the
 * "all categories" adaptive mode know which tags are weak.
 */
import { Grade, type TagSkill } from './types';

/** Expected score (prob. of "knowing") for a skill vs a clue difficulty. */
export function expectedScore(skill: number, difficulty: number): number {
  // Logistic on the 0..1 scale; spread of 4 ≈ a sensible discrimination.
  return 1 / (1 + Math.exp(-4 * (skill - difficulty)));
}

/** Map a grade to an outcome score in [0,1]. */
export function gradeOutcome(grade: Grade): number {
  switch (grade) {
    case Grade.Known:
      return 1;
    case Grade.Shaky:
      return 0.6;
    case Grade.Missed:
      return 0;
  }
}

export function freshTagSkill(tagId: number): TagSkill {
  return {
    tagId,
    rating: 0.5,
    uncertainty: 1,
    attempts: 0,
    recentAccuracy: 0.5,
  };
}

const RECENT_ALPHA = 0.25; // EWMA weight for recent accuracy
const K_FLOOR = 0.08; // minimum learning rate once a tag is well-known
const K_RANGE = 0.32; // additional rate scaled by uncertainty
const UNCERTAINTY_DECAY_B = 0.05; // b in U(n) = 1/(1 + b·n)

/**
 * Update a tag's skill from one graded clue. Pure — returns a new TagSkill.
 * `clueDifficulty` is the clue's df field (0..1).
 */
export function updateTagSkill(
  prev: TagSkill,
  clueDifficulty: number,
  grade: Grade,
): TagSkill {
  const outcome = gradeOutcome(grade);
  const expected = expectedScore(prev.rating, clueDifficulty);

  // K shrinks with confidence: large for the first answers, settling low once a
  // tag is well-attempted. Driven by an uncertainty term that follows the
  // published dynamic-K form U(n) = 1/(1 + b·n) (Pelanek 2016), so the estimate
  // calibrates fast then stabilizes.
  const k = K_FLOOR + K_RANGE * prev.uncertainty;
  const rating = Math.min(Math.max(prev.rating + k * (outcome - expected), 0), 1);

  const attempts = prev.attempts + 1;
  const uncertainty = 1 / (1 + UNCERTAINTY_DECAY_B * attempts);

  const recentAccuracy =
    RECENT_ALPHA * outcome + (1 - RECENT_ALPHA) * prev.recentAccuracy;

  return {
    tagId: prev.tagId,
    rating,
    uncertainty,
    attempts: prev.attempts + 1,
    recentAccuracy,
  };
}

/**
 * Weakness priority for selection: high when the tag is weak AND we're fairly
 * sure it's weak, with an exploration bonus for under-sampled tags. Returns a
 * non-negative weight; callers normalize across candidate tags.
 */
export function weaknessPriority(skill: TagSkill): number {
  const weakness = 1 - skill.rating; // 0 (strong) .. 1 (weak)
  const exploration = 0.35 * skill.uncertainty; // try rarely-seen tags
  return Math.max(0.02, weakness + exploration);
}
